/**
 * AdminEnrollmentsPage — cross-cutting, server-filtered enrollments view.
 *
 * Surfaces every enrollment in the system with the filters admins actually
 * use for summer camp operations:
 *   • Camp (week)          — ?camp_id=...
 *   • Status               — ?status=confirmed|completed|pending|cancelled|waitlisted
 *   • Search (parent/learner/email) — ?search=...
 *   • Date range (registered) — ?from_date=&to_date=
 *   • Item type (camp_full / camp_day / camp_session) — ?item_type=...
 *
 * Row actions: Soft-cancel · Mailto parent · Export CSV.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  Users, Search, Download, X, Loader2, Mail, Ban, ChevronLeft, ChevronRight, Tent,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  confirmed: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-blue-100 text-blue-700',
  pending: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-rose-100 text-rose-700',
  waitlisted: 'bg-purple-100 text-purple-700',
};

const PAGE_SIZE = 25;

export default function AdminEnrollmentsPage() {
  const { api } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [camps, setCamps] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    camp_id: '',
    status: '',
    search: '',
    from_date: '',
    to_date: '',
    item_type: '',
  });
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    api.get('/camps/?active_only=false&page_size=100')
      .then(r => setCamps(r.data?.results || []))
      .catch(() => {});
  }, [api]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page_size', PAGE_SIZE);
    params.set('page', page);
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    return params.toString();
  }, [filters, page]);

  const fetchEnrollments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/enrollments/?${queryString}`);
      setEnrollments(data?.results || []);
      setCount(data?.count || 0);
    } catch {
      toast.error('Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  }, [api, queryString]);

  useEffect(() => { fetchEnrollments(); }, [fetchEnrollments]);

  const setFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setFilter('search', searchInput.trim());
  };

  const clearFilters = () => {
    setFilters({ camp_id: '', status: '', search: '', from_date: '', to_date: '', item_type: '' });
    setSearchInput('');
    setPage(1);
  };

  const cancelEnrollment = async (enr) => {
    if (!window.confirm(`Cancel ${enr.learner_name}'s enrollment at ${enr.section_location}? This soft-cancels the seat and frees it in the section capacity.`)) return;
    setCancelling(enr.id);
    try {
      await api.post(`/enrollments/${enr.id}/cancel/`);
      toast.success('Enrollment cancelled');
      fetchEnrollments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel failed');
    } finally {
      setCancelling(null);
    }
  };

  const exportCSV = () => {
    if (enrollments.length === 0) {
      toast.info('No rows to export');
      return;
    }
    const headers = [
      'Parent', 'Parent Email', 'Parent Phone', 'Learner', 'Grade',
      'Camp / Course', 'Location', 'Sessions', 'Selected Sessions Detail', 'Start Date', 'Amount Paid',
      'Status', 'Registered On',
    ];
    const rows = enrollments.map(e => [
      e.parent_name, e.parent_email, e.parent_phone,
      e.learner_name, e.learner_grade,
      e.section_title, e.section_location,
      e.item_type || '',
      (e.selected_sessions_summary || []).join(' | '),
      e.section_start_date || '',
      e.payment?.amount_total ?? e.payment?.amount ?? '0.00',
      e.status,
      new Date(e.created_at).toISOString().slice(0, 10),
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `enrollments-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row${rows.length === 1 ? '' : 's'}`);
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const hasActiveFilters = Object.values(filters).some(v => v);

  return (
    <div className="space-y-5" data-testid="admin-enrollments-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" /> Enrollments
          </h1>
          <p className="text-sm text-muted-foreground">
            {count} total · server-filtered across all camps
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <form onSubmit={onSearchSubmit} className="flex gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search parent, learner, or email…"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-9"
                data-testid="enrollments-search"
              />
            </div>
            <Button type="submit" variant="outline" size="sm">Search</Button>
          </form>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Camp</Label>
              <Select value={filters.camp_id || 'all'} onValueChange={v => setFilter('camp_id', v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-camp"><SelectValue placeholder="All camps" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All camps</SelectItem>
                  {camps.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filters.status || 'all'} onValueChange={v => setFilter('status', v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="waitlisted">Waitlisted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Session type</Label>
              <Select value={filters.item_type || 'all'} onValueChange={v => setFilter('item_type', v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-item-type"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="camp_full">Full camp</SelectItem>
                  <SelectItem value="camp_day">Single day</SelectItem>
                  <SelectItem value="camp_session">Half-day session</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From date</Label>
              <Input type="date" value={filters.from_date} onChange={e => setFilter('from_date', e.target.value)} data-testid="filter-from-date" />
            </div>
            <div>
              <Label className="text-xs">To date</Label>
              <Input type="date" value={filters.to_date} onChange={e => setFilter('to_date', e.target.value)} data-testid="filter-to-date" />
            </div>
          </div>

          {hasActiveFilters && (
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                <X className="h-3 w-3 mr-1" /> Clear filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : enrollments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>{hasActiveFilters ? 'No enrollments match the current filters.' : 'No enrollments yet.'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Parent</TableHead>
                    <TableHead>Learner</TableHead>
                    <TableHead>Camp · Location</TableHead>
                    <TableHead>Sessions</TableHead>
                    <TableHead>Paid</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead className="w-28 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enrollments.map(e => {
                    const campId = e.camp_section ? camps.find(c => (c.id || '').length && (c.sections || []).some(s => s.id === e.camp_section))?.id : null;
                    return (
                      <TableRow key={e.id} data-testid={`enrollment-row-${e.id}`}>
                        <TableCell>
                          <div className="text-sm font-medium">{e.parent_name || '—'}</div>
                          <div className="text-[11px] text-muted-foreground">{e.parent_email}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">{e.learner_name}</div>
                          <div className="text-[11px] text-muted-foreground">Grade {e.learner_grade || '?'}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{e.section_title}</div>
                          <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                            <Tent className="h-3 w-3" />
                            {campId ? (
                              <Link to={`/admin/camps/${campId}`} className="hover:underline">{e.section_location}</Link>
                            ) : (
                              e.section_location
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">
                          {/* Bug 1 — RSS_Emergent_Fixes_May_11 + May 15 PDF.
                              Now reads from the structured session_details
                              field so each row line shows the specific date
                              + time-range + day's course title. */}
                          {(e.session_details?.length || e.selected_sessions_summary?.length) ? (
                            <div className="space-y-0.5 max-w-[320px]">
                              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                                {e.selected_sessions_count || e.session_details?.length || 0} session
                                {(e.selected_sessions_count || e.session_details?.length) === 1 ? '' : 's'}
                                {e.session_type ? ` · ${e.session_type.replace('_', ' ')}` : ''}
                              </div>
                              {(e.session_details || []).slice(0, 4).map((s, i) => {
                                // Format ISO date "2026-08-03" → "Aug 3" using
                                // local-date parsing to avoid UTC drift.
                                let dateLabel = '';
                                if (s.session_date) {
                                  const m = String(s.session_date).match(/^(\d{4})-(\d{2})-(\d{2})/);
                                  if (m) {
                                    dateLabel = new Date(+m[1], +m[2] - 1, +m[3])
                                      .toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                  }
                                }
                                const fmt = (t) => {
                                  if (!t) return '';
                                  const [h, mm] = String(t).split(':');
                                  const hr = parseInt(h, 10);
                                  const period = hr >= 12 ? 'PM' : 'AM';
                                  const h12 = hr % 12 || 12;
                                  return mm && mm !== '00' ? `${h12}:${mm} ${period}` : `${h12} ${period}`;
                                };
                                const timeRange = s.start_time && s.end_time
                                  ? `${fmt(s.start_time)} – ${fmt(s.end_time)}`
                                  : '';
                                return (
                                  <div key={i} className="text-[11px] text-foreground leading-snug">
                                    <span className="font-medium">{s.day_label}</span>
                                    {dateLabel && <> · {dateLabel}</>}
                                    {timeRange && <> · {timeRange}</>}
                                    {s.session_label && <> · {s.session_label}</>}
                                    {s.course_title && <> — <span className="text-muted-foreground">{s.course_title}</span></>}
                                  </div>
                                );
                              })}
                              {/* Legacy fallback when only the flat string summary exists. */}
                              {!e.session_details?.length && (e.selected_sessions_summary || []).slice(0, 4).map((s, i) => (
                                <div key={`legacy-${i}`} className="text-[11px] text-foreground" title={s}>• {s}</div>
                              ))}
                              {((e.session_details || e.selected_sessions_summary || []).length > 4) && (
                                <div className="text-[10px] text-muted-foreground">
                                  +{(e.session_details || e.selected_sessions_summary).length - 4} more…
                                </div>
                              )}
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-xs">{e.item_type || '—'}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm font-medium" data-testid={`paid-${e.id}`}>
                          {e.payment_amount != null
                            ? `$${Number(e.payment_amount).toFixed(2)}`
                            : '—'}
                        </TableCell>
                        <TableCell><Badge className={STATUS_STYLES[e.status] || ''}>{e.status}</Badge></TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(e.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex gap-1">
                            {e.parent_email && (
                              <a href={`mailto:${e.parent_email}`} title="Email parent">
                                <Button size="sm" variant="ghost" data-testid={`email-parent-${e.id}`}>
                                  <Mail className="h-3 w-3" />
                                </Button>
                              </a>
                            )}
                            {!['cancelled'].includes(e.status) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive"
                                disabled={cancelling === e.id}
                                onClick={() => cancelEnrollment(e)}
                                data-testid={`cancel-enr-${e.id}`}
                                title="Soft-cancel enrollment"
                              >
                                {cancelling === e.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {count > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {page} of {totalPages} · {count} total
          </span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page-btn">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page-btn">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
